/*
  Eva Källare film archive. Ported from a standalone Lovable/React app
  (CRTVideo.tsx + src/data/videos.ts) into vanilla JS + Liquid + CSS --
  no React runtime, no Radix/shadcn.

  Core mechanic (REBUILT 2026-07-27, complete spec -- supersedes the
  2026-07-24/07-26 rounds' "TV never initiates, pick from a list" model
  entirely): the small in-room TV IS the entry point. A visible idle
  tape sits by it; clicking either the tape or the TV
  (blocks/ai_gen_block_aa57325.liquid's insertTape()) flies the tape
  into the small screen, clunk, static blip, then the TV pops (cartoon-
  overshoot FLIP) into a big player -- ONCE per session. The pop reveals
  a VCR on-screen menu rendered inside the big player's own screen
  (built by renderOsd() below into window.EvaKallareTV.osdTabs/osdList);
  picking a film from there is a lightweight "channel change"
  (playChannel() below), NOT a repeat of the pop -- no per-film insert/
  eject choreography anymore. A contextual corner button toggles between
  "back to menu" (while playing, stays popped) and "eject" (while on the
  menu, full reverse-pop back to the dormant small TV).

  kallare-films.json (assets/) is the single source of truth for the
  film list -- schema v1, {"films": [{id, title, url, type, category,
  decade}, ...]}, same fetch-once pattern as eva-tapes.json. No admin
  editor exists (the original Lovable app's localStorage-backed one was
  dropped per Per -- he never used it); list changes happen by request,
  see CLAUDE.md's "add a film to the källare" section.
*/
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  var ROOT_SELECTOR = '[data-kallare-archive-root]';
  var ITEM_SELECTOR = '[data-kallare-film-item]';
  var DECADE_ORDER = ['80s', '90s', '2000s', '2010s', '2020s'];
  var DECADE_LABELS = { '80s': "80's", '90s': "90's", '2000s': "00's", '2010s': "10's", '2020s': "20's" };

  // --- Manifest (kallare-films.json), fetched once ------------------------

  var films = null;
  var filmsById = {};
  var fetchInFlight = null;

  function fetchFilms(cb) {
    if (films) { cb(films); return; }
    var url = window.EvaKallareFilmsManifestUrl;
    if (!url) { return; }
    if (!fetchInFlight) {
      fetchInFlight = fetch(url)
        .then(function (res) { return res.json(); })
        .then(function (data) {
          films = (data && data.films) || [];
          filmsById = {};
          films.forEach(function (f) { filmsById[f.id] = f; });
        })
        .catch(function () {
          films = [];
        });
    }
    fetchInFlight.then(function () { cb(films); });
  }

  // Adapted from the original CRTVideo.tsx's getEmbedUrl -- autoplay=1
  // added 2026-07-28 (audio-orchestration spec point 1). Only actually
  // used for Vimeo films and as a defensive fallback for YouTube ones
  // (see setYoutubeFilm() below, which normally drives YouTube playback
  // through the IFrame Player API instead of a raw src for real
  // autoplay-state feedback).
  function embedUrl(film) {
    if (film.type === 'youtube') {
      // playsinline=1 added 2026-07-29 -- without it iOS hijacks this
      // (rare, defensive-fallback-only) path into the native fullscreen
      // player instead of playing inline, same reasoning as the IFrame
      // Player API's own playerVars.playsinline below.
      return film.url + '?rel=0&modestbranding=1&autoplay=1&playsinline=1';
    }
    return film.url + '?title=0&byline=0&portrait=0&autoplay=1';
  }

  // --- Mechanical sounds: reuses eva-radio-tape.js's own asset family ------
  // (window.EvaRadioAssets, set up in theme.liquid) -- no new sound assets,
  // same gain calibration (0.07) that file's own comments cite for this WAV
  // set. mechStop is the eject click (the ONE-TIME entry's own insert clunk
  // is a separate instance the block owns directly, see insertTape() there).

  var MECH_GAIN = 0.07;
  var seatAudio = null;
  var reduceMotion = false;
  try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  (function initMechAudio() {
    var assets = window.EvaRadioAssets || {};
    try {
      if (assets.mechStop) {
        seatAudio = new Audio(assets.mechStop);
        seatAudio.preload = 'auto';
        if (window.EvaMute) { window.EvaMute.applyToElement(seatAudio); }
      }
    } catch (e) {}
  })();

  function playMech(audio) {
    if (!audio) { return; }
    try {
      audio.pause();
      audio.currentTime = 0;
      audio.volume = MECH_GAIN;
      var p = audio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }

  // --- Audio orchestration (2026-07-28, mute unified 2026-08-02,
  // TV-scoped mute UN-unified 2026-08-13) --------------------------------
  // evaKallareAmbienceMuted (the room's own top-left toggle, PAUSES the
  // basement ambience loop -- a real stop/start, not a silence) stays a
  // separate, independent control -- out of scope for the global mute
  // below, same as the radio widget's own open/closed minimize toggle.
  //
  // The per-selection insert sound and the MENU/eject mechanical click
  // share ONE source of truth, window.EvaMute (assets/eva-mute.js,
  // evaSoundMuted under the hood), per Per's 2026-08-02 spec: "every
  // audio source subscribes to it rather than keeping its own
  // independent state."
  //
  // The EVATV song and film audio (ytPlayer / the Vimeo iframe) are
  // DIFFERENT: they respond to window.EvaMute too (global mute still
  // silences everything, unchanged), but ALSO to their own scoped flag,
  // tvAudioMuted, driven by the OSD's own MUTE button via the
  // 'eva:kallare:osd-mute-toggle' event -- see updateTvAudioMuteState()
  // below. The 2026-08-02 unification had made the OSD button a thin
  // proxy on window.EvaMute.setMuted(), which meant pressing it silenced
  // the ENTIRE site (hover/click UI blips, the Radio) -- a real bug, not
  // the intended "mute the TV" behavior, fixed 2026-08-13 by giving this
  // pair their own combined-OR mute state again instead of relying on
  // EvaMute.applyToElement's single-source sync.

  var EVATV_GAIN = 0.35; // "sits slightly below film-audio level -- menu music, not a concert"

  var evatvAudio = null;
  var vhsInsertAudio = null;
  (function initOrchestrationAudio() {
    var assets = window.EvaKallareAssets || {};
    try {
      if (assets.evatvSong) {
        evatvAudio = new Audio(assets.evatvSong);
        evatvAudio.loop = true;
        evatvAudio.volume = EVATV_GAIN;
        evatvAudio.preload = 'auto';
        // NOT window.EvaMute.applyToElement -- evatvAudio's muted state
        // is driven by updateTvAudioMuteState() (global OR TV-scoped),
        // not a single-source sync straight off the global flag. Applied
        // once here too (function declaration below, hoisted, callable
        // already) so a returning muted visitor's very first evatvAudio
        // element starts muted, same as applyToElement used to guarantee.
        updateTvAudioMuteState();
      }
    } catch (e) {}
    try {
      if (assets.vhsInsert) {
        vhsInsertAudio = new Audio(assets.vhsInsert);
        vhsInsertAudio.preload = 'auto';
        if (window.EvaMute) { window.EvaMute.applyToElement(vhsInsertAudio); }
      }
    } catch (e) {}
  })();

  function isUiSoundMuted() {
    return window.EvaMute ? window.EvaMute.isMuted() : false;
  }

  // TV-scoped mute -- the OSD's own MUTE button, independent of the
  // global evaSoundMuted flag. Reset to false on every eject/PJAX nav
  // (see onEject()/init() below), "state persists while the player is
  // up," not across a full eject/re-entry -- same design the button had
  // before the 2026-08-02 rewire.
  var tvAudioMuted = false;

  // Single place that computes "should EVATV song / film audio actually
  // be silent right now" (global mute OR the TV's own scoped flag) and
  // applies it to every live audio surface that carries that audio.
  // Called from both mute sources (the global 'eva:sound:muted' event
  // and the OSD's 'eva:kallare:osd-mute-toggle' event) plus anywhere a
  // player is freshly created, so a not-yet-existing ytPlayer/iframe
  // always picks up the CURRENT combined state the moment it exists.
  function updateTvAudioMuteState() {
    var muted = tvAudioMuted || (window.EvaMute && window.EvaMute.isMuted());
    if (evatvAudio) { evatvAudio.muted = muted; }
    if (ytPlayer) {
      try { if (muted) { ytPlayer.mute(); } else { ytPlayer.unMute(); } } catch (e) {}
    }
    if (muted) { hideSoundOnPrompt(); }
    applyMuteToPlainIframe(getMount(), muted);
  }

  // Resumes from wherever currentTime is (a paused-not-reset song picks up
  // where it left off) -- see pauseEvatvSong() vs stopEvatvSongFully()
  // below for which callers reset the position and which don't. Always
  // attempts play() regardless of mute state -- muted-but-playing is
  // exactly what "mute -> open Basement -> TV pops up silent" (Per's test
  // matrix) requires: evatvAudio.muted (kept in sync by
  // updateTvAudioMuteState() above) does the actual silencing, so a later
  // unmute finds the song already running and picks it up in place
  // instead of needing a fresh, potentially gesture-blocked play() call.
  function startEvatvSong() {
    if (!evatvAudio) { return; }
    try {
      var p = evatvAudio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }

  // Handoff-chain step ("stop EVATV song") and MENU-mid-film step (film
  // stops, song later RESUMES, spec point 6) -- position is deliberately
  // left where it was, not reset, so "resumes" reads as a real resume.
  function pauseEvatvSong() {
    if (!evatvAudio) { return; }
    try { evatvAudio.pause(); } catch (e) {}
  }

  // Eject default ("song stops") -- a real stop, not a pause-to-resume:
  // the next pop (re-entering after a full eject) starts the song fresh
  // rather than picking up mid-track from a session that's already over.
  function stopEvatvSongFully() {
    if (!evatvAudio) { return; }
    try { evatvAudio.pause(); evatvAudio.currentTime = 0; } catch (e) {}
  }

  // Insert/clunk sound, spec point 2 -- "every time" a film is selected,
  // not just the one-time entry (which the block's own insertClunkAudio
  // already covers via a separate Audio instance on the same asset).
  function playInsertSound() {
    if (!vhsInsertAudio || isUiSoundMuted()) { return; }
    try {
      vhsInsertAudio.pause();
      vhsInsertAudio.currentTime = 0;
      vhsInsertAudio.volume = MECH_GAIN;
      var p = vhsInsertAudio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }

  // First pop only fires once per session's entry (see popPlayer()'s own
  // comment in the block) -- this listener just starts the song every
  // time that happens, including a fresh re-entry after a full eject.
  // warmYoutubePlayer() (2026-07-29, mobile autoplay fix) is declared
  // further below -- also called from here since the pop is itself a
  // real user gesture, the same reasoning that makes this the right
  // moment to pre-create the player rather than waiting for a film pick.
  document.addEventListener('eva:kallare:popped', function () {
    startEvatvSong();
    warmYoutubePlayer();
  });

  // --- YouTube IFrame Player API (autoplay + mute-fallback detection) ------
  // Spec point 1: selecting a film must autoplay WITH SOUND where the
  // browser allows it (gesture-created iframes do, in practice), and fall
  // back gracefully -- never silently -- when it doesn't. A raw <iframe
  // src="...&autoplay=1"> gives this page zero visibility into which of
  // those happened (cross-origin, no load/error signal for "played but
  // muted"). YouTube's own IFrame Player API does expose that via
  // onStateChange + player.isMuted(): when unmuted autoplay is blocked,
  // YouTube's player automatically retries muted rather than failing
  // outright, so a PLAYING state with isMuted() true is the real,
  // observed signal to show the "SOUND ON" prompt -- not a timer guess.
  // 81 of 92 films in kallare-films.json are youtube vs. 11 vimeo, so
  // this integration covers the large majority; Vimeo is intentionally
  // left on the simpler best-effort embedUrl()+autoplay=1 path above,
  // with no state detection -- flagged to Per as a scope limitation
  // rather than silently different behavior.

  var YT_API_URL = 'https://www.youtube.com/iframe_api';
  var ytApiReady = false;
  var ytApiLoading = false;
  var ytApiCallbacks = [];
  var ytPlayer = null;
  var soundOnPromptEl = null;
  // Play fallback (2026-07-29, mobile autoplay fix, spec point 4): "never
  // a dead TV" -- if a real PLAYING/BUFFERING state hasn't been observed
  // shortly after a film is picked, show a manual play button instead of
  // leaving a black frame. See scheduleFallbackCheck()/showPlayFallback()
  // below, declared near destroyYtPlayer() since they share its state.
  var playFallbackEl = null;
  var playFallbackTimer = null;
  var pendingPlayFilm = null;

  function loadYoutubeApi(cb) {
    if (ytApiReady && window.YT && window.YT.Player) { cb(); return; }
    ytApiCallbacks.push(cb);
    if (ytApiLoading) { return; }
    ytApiLoading = true;
    var previousReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = function () {
      ytApiReady = true;
      if (typeof previousReady === 'function') { try { previousReady(); } catch (e) {} }
      var callbacks = ytApiCallbacks;
      ytApiCallbacks = [];
      callbacks.forEach(function (fn) { fn(); });
    };
    var tag = document.createElement('script');
    tag.src = YT_API_URL;
    document.head.appendChild(tag);
  }

  function youtubeIdFromUrl(url) {
    var m = /embed\/([^?&/]+)/.exec(url || '');
    return m ? m[1] : null;
  }

  function hideSoundOnPrompt() {
    if (soundOnPromptEl) { soundOnPromptEl.classList.remove('kallare-sound-on-prompt--visible'); }
  }

  function showSoundOnPrompt(mount) {
    if (!mount) { return; }
    if (!soundOnPromptEl) {
      soundOnPromptEl = document.createElement('button');
      soundOnPromptEl.type = 'button';
      soundOnPromptEl.className = 'kallare-sound-on-prompt';
      soundOnPromptEl.textContent = 'SOUND ON';
      soundOnPromptEl.addEventListener('click', function (event) {
        event.stopPropagation();
        if (ytPlayer && typeof ytPlayer.unMute === 'function') {
          try { ytPlayer.unMute(); ytPlayer.setVolume(100); } catch (e) {}
        }
        hideSoundOnPrompt();
      });
    }
    if (soundOnPromptEl.parentNode !== mount) { mount.appendChild(soundOnPromptEl); }
    soundOnPromptEl.classList.add('kallare-sound-on-prompt--visible');
  }

  // Applies the CURRENT combined mute state (global OR the TV's own
  // scoped flag) to a just-constructed player -- the IFrame Player API's
  // methods aren't safe to call until onReady fires, so this can't just
  // happen inline where `new window.YT.Player` is called. Both creation
  // sites below wire this as their onReady. Deliberately only ever calls
  // mute(), never unMute(), here -- unlike updateTvAudioMuteState() (used
  // by the real mute/unmute toggle events below), forcing an unMute() at
  // creation time would fight YouTube's own autoplay-blocked-so-retry-
  // muted fallback and corrupt the isMuted() signal onYtPlayerStateChange
  // relies on to decide whether to show the "SOUND ON" prompt.
  function onYtPlayerReady(event) {
    try {
      var muted = tvAudioMuted || (window.EvaMute && window.EvaMute.isMuted());
      if (muted) { event.target.mute(); }
    } catch (e) {}
  }

  function onYtPlayerStateChange(event) {
    if (!window.YT || event.data !== window.YT.PlayerState.PLAYING) { return; }
    // A real PLAYING state (muted or not) means autoplay worked at some
    // level -- the play-fallback button's whole job is done, hide it
    // regardless of which of the two outcomes below this turns out to be.
    hidePlayFallback();
    var muted = false;
    try { muted = event.target.isMuted(); } catch (e) {}
    if (muted) {
      showSoundOnPrompt(getMount());
    } else {
      hideSoundOnPrompt();
    }
  }

  function destroyYtPlayer() {
    if (ytPlayer) {
      try { ytPlayer.destroy(); } catch (e) {}
      ytPlayer = null;
    }
    hideSoundOnPrompt();
  }

  // Pre-creates the YT.Player instance (2026-07-29, mobile autoplay fix,
  // spec point 3) the moment the OSD becomes the active surface -- always
  // itself right after a real user gesture (the tap that pops the TV, or
  // the MENU button's own tap, see the two call sites below) -- rather
  // than waiting until a film is actually picked. This is what makes
  // playChannel()'s later loadVideoById() call (in setYoutubeFilm()) a
  // plain synchronous method call on an ALREADY-CONSTRUCTED player,
  // instead of also having to wait on loadYoutubeApi()'s own async
  // <script> tag load -- THAT wait, not just the setTimeout playChannel()
  // used to gate playback behind, is what pushed the very first
  // selection in a session outside the gesture window regardless of
  // ordering fixes there. No videoId in playerVars here -- an empty
  // player, nothing to autoplay yet; setYoutubeFilm() loads the actual
  // video later via loadVideoById() once a film is picked.
  function warmYoutubePlayer() {
    if (ytPlayer) { return; }
    var mount = getMount();
    if (!mount) { return; }
    loadYoutubeApi(function () {
      // A very fast film pick may have already gone through
      // setYoutubeFilm()'s own on-demand fallback path and created
      // ytPlayer before this callback got a chance to fire -- don't
      // clobber it with a second, empty player.
      if (ytPlayer) { return; }
      var wrap = mount.querySelector('.kallare-yt-wrap');
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'kallare-yt-wrap';
        mount.appendChild(wrap);
      }
      var target = document.createElement('div');
      wrap.appendChild(target);
      ytPlayer = new window.YT.Player(target, {
        playerVars: {
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
          playsinline: 1,
          origin: window.location.origin
        },
        events: {
          onReady: onYtPlayerReady,
          onStateChange: onYtPlayerStateChange
        }
      });
    });
  }

  // --- Play fallback (2026-07-29, mobile autoplay fix, spec point 4) -------
  // "Never a dead TV": scheduleFallbackCheck() arms a short watchdog right
  // after a film is picked; if a real PLAYING/BUFFERING state hasn't
  // landed by then, showPlayFallback() puts a manual play button over the
  // frame instead of leaving it black. YouTube only -- Vimeo's plain-
  // iframe path has no state signal at all (same disclosed scope gap the
  // SOUND ON prompt above already lives with), so there's no reliable way
  // to tell "playing silently" from "blocked" there; showing a fallback
  // unconditionally would misfire over films that are actually playing
  // fine.
  function hidePlayFallback() {
    if (playFallbackTimer) { clearTimeout(playFallbackTimer); playFallbackTimer = null; }
    pendingPlayFilm = null;
    if (playFallbackEl) { playFallbackEl.classList.remove('kallare-play-fallback--visible'); }
  }

  function showPlayFallback(mount) {
    if (!mount) { return; }
    if (!playFallbackEl) {
      playFallbackEl = document.createElement('button');
      playFallbackEl.type = 'button';
      playFallbackEl.className = 'kallare-play-fallback';
      playFallbackEl.setAttribute('aria-label', 'Play');
      playFallbackEl.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="M8 5v14l11-7z" fill="currentColor" />' +
        '</svg>';
      playFallbackEl.addEventListener('click', function (event) {
        event.stopPropagation();
        var film = pendingPlayFilm;
        hidePlayFallback();
        if (ytPlayer && typeof ytPlayer.playVideo === 'function') {
          // Direct, synchronous call from this exact tap -- a fresh user
          // gesture in its own right, so this works even when the
          // original selection's autoplay genuinely got blocked.
          try { ytPlayer.playVideo(); } catch (e) {}
        } else if (film) {
          // ytPlayer somehow still doesn't exist (warming never
          // finished) -- fall all the way back to a full retry, itself
          // gesture-linked from this tap.
          setIframeFilm(film);
        }
      });
    }
    if (playFallbackEl.parentNode !== mount) { mount.appendChild(playFallbackEl); }
    playFallbackEl.classList.add('kallare-play-fallback--visible');
  }

  function scheduleFallbackCheck(film) {
    hidePlayFallback();
    if (film.type !== 'youtube') { return; }
    pendingPlayFilm = film;
    playFallbackTimer = setTimeout(function () {
      playFallbackTimer = null;
      // Stale check -- the channel moved on (or MENU/eject fired) since
      // this was armed, nothing to do.
      if (!pendingPlayFilm || currentFilmId !== pendingPlayFilm.id) { return; }
      var state = -99;
      if (ytPlayer && typeof ytPlayer.getPlayerState === 'function') {
        try { state = ytPlayer.getPlayerState(); } catch (e) {}
      }
      var isPlayingOrBuffering = window.YT && (state === window.YT.PlayerState.PLAYING || state === window.YT.PlayerState.BUFFERING);
      if (isPlayingOrBuffering) {
        pendingPlayFilm = null;
      } else {
        showPlayFallback(getMount());
      }
    }, 1500);
  }

  // --- Player mount / iframe ------------------------------------------------
  // Big player (2026-07-25): real playback now lives exclusively in the
  // large bezel overlay (blocks/ai_gen_block_aa57325.liquid's
  // window.EvaKallareTV.bigPlayerMount) -- the small in-room screen's own
  // [data-kallare-player] div is legacy idle-CSS-only real estate now, it
  // never hosts an <iframe> again. Falls back to the old selector only if
  // EvaKallareTV somehow isn't ready yet, which shouldn't normally happen
  // (the block's connectedCallback runs before this file's init()).

  function getMount() {
    var tv = window.EvaKallareTV;
    if (tv && tv.bigPlayerMount) { return tv.bigPlayerMount; }
    return document.querySelector('[data-kallare-player]');
  }

  // Best-effort global-mute support for the plain-iframe (Vimeo) path --
  // no IFrame Player API here, only Vimeo's long-standing postMessage
  // protocol, which its embedded player listens for without needing an
  // SDK script load. Fire-and-forget: cross-origin, so there's no
  // reliable readiness handshake without loading Vimeo's own player.js,
  // which felt like too much new surface for 11 of 92 films -- same
  // "disclosed, deliberate scope gap" precedent embedUrl()'s own comment
  // already sets for Vimeo's autoplay-state detection. Silently a no-op
  // if the iframe isn't a Vimeo one or hasn't finished its own internal
  // load yet; the next state-change event resends it.
  function applyMuteToPlainIframe(mount, muted) {
    if (!mount) { return; }
    var iframe = mount.querySelector(':scope > iframe');
    if (!iframe || !iframe.src || iframe.src.indexOf('player.vimeo.com') === -1) { return; }
    try {
      iframe.contentWindow.postMessage(JSON.stringify({ method: 'setVolume', value: muted ? 0 : 1 }), '*');
    } catch (e) {}
  }

  // Plain-iframe path -- Vimeo films, and a defensive fallback if a
  // YouTube film's video ID somehow can't be parsed out of its URL.
  function setPlainIframeFilm(mount, film) {
    destroyYtPlayer();
    var ytWrap = mount.querySelector('.kallare-yt-wrap');
    if (ytWrap) { ytWrap.parentNode.removeChild(ytWrap); }
    var iframe = mount.querySelector(':scope > iframe');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen'
      );
      iframe.setAttribute('allowfullscreen', '');
      mount.appendChild(iframe);
    }
    iframe.title = film.title;
    iframe.src = embedUrl(film);
    if (tvAudioMuted || (window.EvaMute && window.EvaMute.isMuted())) {
      setTimeout(function () { applyMuteToPlainIframe(mount, true); }, 800); // best-effort: give the embed a moment to attach its own postMessage listener
    }
  }

  // YouTube path -- routes through the IFrame Player API (see the block
  // of comments above onYtPlayerStateChange) instead of a raw iframe src,
  // so autoplay-with-sound vs. autoplay-muted is an observed fact, not a
  // guess. Reuses a single YT.Player across channel switches via
  // loadVideoById (which itself autoplays the new video) rather than
  // tearing down and recreating the player every time a film is picked.
  function setYoutubeFilm(mount, film) {
    var videoId = youtubeIdFromUrl(film.url);
    if (!videoId) { setPlainIframeFilm(mount, film); return; }

    var plainIframe = mount.querySelector(':scope > iframe');
    if (plainIframe) { plainIframe.parentNode.removeChild(plainIframe); }

    loadYoutubeApi(function () {
      // A channel switch to another YouTube film may land here after an
      // eject destroyed the previous player -- re-check on arrival, not
      // just at call time, since loadYoutubeApi's callback can fire well
      // after this function returns.
      if (ytPlayer) {
        hideSoundOnPrompt();
        try { ytPlayer.loadVideoById(videoId); } catch (e) {}
        return;
      }
      var wrap = mount.querySelector('.kallare-yt-wrap');
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.className = 'kallare-yt-wrap';
        mount.appendChild(wrap);
      }
      var target = document.createElement('div');
      wrap.appendChild(target);
      ytPlayer = new window.YT.Player(target, {
        videoId: videoId,
        playerVars: {
          autoplay: 1,
          rel: 0,
          modestbranding: 1,
          enablejsapi: 1,
          playsinline: 1,
          origin: window.location.origin
        },
        events: {
          onReady: onYtPlayerReady,
          onStateChange: onYtPlayerStateChange
        }
      });
    });
  }

  function setIframeFilm(film) {
    var mount = getMount();
    if (!mount) { return; }
    if (film.type === 'youtube') {
      setYoutubeFilm(mount, film);
    } else {
      setPlainIframeFilm(mount, film);
    }
  }

  // Stops whatever's currently playing (either playback mechanism) --
  // used by the MENU button's show-menu path, spec point 6's "film
  // stops" half.
  function stopCurrentPlayback() {
    destroyYtPlayer();
    var mount = getMount();
    if (!mount) { return; }
    var iframe = mount.querySelector(':scope > iframe');
    if (iframe) { iframe.src = 'about:blank'; }
  }

  // --- Now-playing marker + list lookups ------------------------------------

  var currentFilmId = null;

  function updateNowPlaying(filmId) {
    var root = document.querySelector(ROOT_SELECTOR);
    if (!root) { return; }
    var items = root.querySelectorAll(ITEM_SELECTOR);
    for (var i = 0; i < items.length; i++) {
      var isCurrent = items[i].getAttribute('data-film-id') === filmId;
      items[i].classList.toggle('is-playing', isCurrent);
      items[i].setAttribute('aria-current', isCurrent ? 'true' : 'false');
    }
  }

  // --- Entry / channel switching (rebuilt 2026-07-27, complete spec) --------
  // "The machine is on; films are channels" -- picking a film from the OSD
  // (which only ever exists once popped) is a lightweight content swap, NOT
  // a repeat of the insert/pop sequence. The flying-tape animation and
  // mechanical tick+clunk now live exclusively in the block's own
  // insertTape() (the ONE-TIME entry, small-TV-tape-to-big-player),
  // triggered by clicking the idle tape or the TV hotspot -- neither lives
  // in this file anymore.

  var busy = false;

  // A brief static-flicker transition between channels -- .kallare-channel-
  // flicker is a plain/static class (not {{ ai_gen_id }}-suffixed) applied
  // to window.EvaKallareTV.bigPlayerMount, same "this file can't know a
  // Liquid-interpolated class name" reasoning the OSD's own row/tab classes
  // already established.
  function playChannel(filmId, fromEl) {
    if (busy) { return; }
    var film = filmsById[filmId];
    if (!film) { return; }
    var tv = window.EvaKallareTV;
    if (!tv) { return; }
    busy = true;

    // Defensive only -- the OSD (and therefore this call) shouldn't be
    // reachable before the entry sequence has popped the player, but if it
    // somehow is, run the real entry first rather than silently failing.
    if (!tv.isPopped()) {
      if (tv.insertTape) {
        tv.insertTape(function () { busy = false; playChannel(filmId, fromEl); });
      } else {
        busy = false;
      }
      return;
    }

    // Mobile autoplay fix (2026-07-29): setIframeFilm() -- the actual
    // playVideo()/loadVideoById() call -- MUST run first, synchronously,
    // still inside this click's own user-gesture call stack. This used
    // to run inside the setTimeout below instead; a setTimeout callback
    // is a macrotask on a LATER event-loop tick, which iOS/Android no
    // longer count as gesture-linked for autoplay-with-sound, even
    // though desktop browsers are lax enough not to care -- exactly why
    // this worked on desktop and silently failed on a real phone.
    // Everything else (the audio handoff, the flicker) now layers ON TOP
    // of the play call instead of gating it behind their own delay.
    setIframeFilm(film);
    currentFilmId = film.id;
    updateNowPlaying(film.id);
    updateOsdNowPlaying(film.id);
    tv.wake();
    scheduleFallbackCheck(film);

    // Handoff chain (spec point 4): stop EVATV song -> insert sound +
    // static, immediately after the play call above rather than ahead of
    // it. Real film audio has its own network/buffering startup lag
    // regardless of exactly when playVideo() was called, so this
    // reordering doesn't reintroduce the audio-overlap the original
    // 160ms gate was guarding against -- confirmed live against real
    // playback, not just reasoned about.
    pauseEvatvSong();
    playInsertSound();

    var mount = getMount();
    if (mount) {
      mount.classList.add('kallare-channel-flicker');
      setTimeout(function () {
        mount.classList.remove('kallare-channel-flicker');
        busy = false;
      }, 160);
    } else {
      busy = false;
    }
  }

  // --- Menu button: show-menu / eject (2026-07-27) --------------------------
  // Responds to the contextual corner button's two dispatched events (blocks/
  // ai_gen_block_aa57325.liquid) -- this file owns the mechanical sounds and
  // the iframe's actual lifecycle, the block owns the TV's own DOM/animation
  // (same split ejectToMenu() established last round, just two events now
  // instead of one, matching the button's own two states).

  // "show-menu": stop the current channel (still popped), return to the OSD.
  // Clears the iframe's src rather than removing the element so
  // ensureIframe() can just reuse it next time -- playback genuinely stops,
  // not just hides behind the menu.
  function onShowMenu() {
    playMech(seatAudio);
    hidePlayFallback();
    stopCurrentPlayback();
    currentFilmId = null;
    updateOsdNowPlaying(null);
    startEvatvSong(); // spec point 6 default: "film stops, EVATV song resumes with the menu"
    // stopCurrentPlayback() (above) just destroyed the warmed player via
    // destroyYtPlayer() -- re-warm immediately (2026-07-29, mobile
    // autoplay fix) so the NEXT film pick from this menu is just as
    // gesture-safe as the first one was, not a regression back to the
    // on-demand creation path every time the user bounces to the menu
    // and back.
    warmYoutubePlayer();
    var tv = window.EvaKallareTV;
    if (tv && tv.showMenu) { tv.showMenu(); }
  }
  document.addEventListener('eva:kallare:show-menu', onShowMenu);

  // "eject": full reverse -- already on the OSD (per the button's own
  // contextual logic, this event only ever fires from that state, so the
  // iframe's already cleared) -- just the mechanical eject sound, the
  // song stopping for real, plus the block's own reverse-pop animation
  // (which itself resumes the room ambience if it was playing before the
  // pop -- see ejectPlayer()/resumeAmbienceIfNeeded() in the block).
  function onEject() {
    // Mobile browse panel: eject always wins over browse, instantly (no
    // second animation racing the block's own reverse-pop below) -- see
    // closeBrowsePanelInstant()'s own comment.
    closeBrowsePanelInstant();
    playMech(seatAudio);
    hidePlayFallback();
    currentFilmId = null;
    busy = false;
    stopEvatvSongFully();
    // TV-scoped mute resets on eject -- "state persists while the player
    // is up," not across a full eject/re-entry, mirrors the block's own
    // reset of the button's UI/local flag in finishEject(). Global mute
    // is untouched either way (that one really does persist site-wide).
    tvAudioMuted = false;
    updateTvAudioMuteState();
    var tv = window.EvaKallareTV;
    if (tv && tv.eject) { tv.eject(); }
  }
  document.addEventListener('eva:kallare:eject', onEject);

  // Film audio -- YouTube path: the IFrame Player API has no <audio>
  // element for EvaMute.applyToElement to grab, so this applies the
  // CURRENT combined mute state (global OR the TV's own scoped flag) to
  // whichever ytPlayer instance is currently live via its own mute()/
  // unMute() methods, same as the Vimeo iframe's postMessage path.
  // updateTvAudioMuteState() reads the `ytPlayer` closure var live, so it
  // stays correct across destroyYtPlayer()/re-creation without needing
  // to re-bind. Muted-ON also hides the "SOUND ON" prompt (nothing to
  // invite the user to unmute while audio's explicitly muted); the
  // resulting unMute() calls (global going off, or the OSD toggle going
  // off) happen synchronously inside the click handler that dispatched
  // this event, still within that same user gesture, which satisfies
  // browser autoplay-unmute policy same as the prompt's own click
  // handler does.
  document.addEventListener('eva:sound:muted', function () {
    updateTvAudioMuteState();
  });

  // OSD MUTE button (blocks/ai_gen_block_aa57325.liquid) -- TV-scoped,
  // deliberately NOT window.EvaMute. See the big comment above
  // tvAudioMuted's declaration for why this exists as its own flag again
  // (2026-08-13) instead of proxying the global mute.
  document.addEventListener('eva:kallare:osd-mute-toggle', function (e) {
    tvAudioMuted = !!(e.detail && e.detail.muted);
    updateTvAudioMuteState();
  });

  // --- Archive list rendering ------------------------------------------------

  function vhsBadgeSvg() {
    return (
      '<svg viewBox="0 0 24 16" aria-hidden="true" focusable="false">' +
      '<rect x="1" y="1" width="22" height="14" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
      '<circle cx="7" cy="8" r="2.6" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
      '<circle cx="17" cy="8" r="2.6" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
      '</svg>'
    );
  }

  function discBadgeSvg() {
    return (
      '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">' +
      '<circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" stroke-width="1.4"/>' +
      '<circle cx="8" cy="8" r="1.6" fill="currentColor"/>' +
      '</svg>'
    );
  }

  function buildFilmItem(film) {
    var isVhs = film.category === 'VHS';
    var el = document.createElement('button');
    el.type = 'button';
    el.className = 'kallare-film' + (isVhs ? ' kallare-film--vhs' : ' kallare-film--flat');
    el.setAttribute('data-kallare-film-item', '');
    el.setAttribute('data-film-id', film.id);
    el.setAttribute('aria-current', 'false');
    el.innerHTML =
      '<span class="kallare-film__cassette" aria-hidden="true">' +
        '<span class="kallare-film__badge">' + (isVhs ? vhsBadgeSvg() : discBadgeSvg()) + '</span>' +
      '</span>' +
      '<span class="kallare-film__title">' + film.title.replace(/</g, '&lt;') + '</span>' +
      '<span class="kallare-film__now-playing" aria-hidden="true">SPELAS NU</span>';
    el.addEventListener('click', function () {
      playChannel(film.id, el.querySelector('.kallare-film__cassette') || el);
    });
    return el;
  }

  function buildDecadeGroup(decade, decadeFilms) {
    var group = document.createElement('div');
    group.className = 'kallare-decade-group';
    var heading = document.createElement('h3');
    heading.className = 'kallare-decade-group__heading';
    heading.textContent = DECADE_LABELS[decade] || decade;
    var shelf = document.createElement('div');
    shelf.className = 'kallare-decade-group__shelf';
    decadeFilms.forEach(function (film) {
      shelf.appendChild(buildFilmItem(film));
    });
    group.appendChild(heading);
    group.appendChild(shelf);
    return group;
  }

  function renderArchive(root, filmList) {
    root.innerHTML = '';
    var byDecade = {};
    filmList.forEach(function (f) {
      (byDecade[f.decade] = byDecade[f.decade] || []).push(f);
    });
    DECADE_ORDER.forEach(function (decade) {
      if (byDecade[decade] && byDecade[decade].length) {
        root.appendChild(buildDecadeGroup(decade, byDecade[decade]));
      }
    });
    // Any decade value present in the data but not in DECADE_ORDER (future-proofing) -- append at the end rather than silently dropping it.
    Object.keys(byDecade).forEach(function (decade) {
      if (DECADE_ORDER.indexOf(decade) === -1) {
        root.appendChild(buildDecadeGroup(decade, byDecade[decade]));
      }
    });
  }

  // --- VCR on-screen menu (2026-07-26) --------------------------------------
  // Replaces renderArchive()/the below-the-room list as the ACTIVE film-
  // picking surface -- the small in-room TV is now the sole interface.
  // renderArchive() and initPageWideCursor() above are left completely
  // inert, not deleted (removing "kallare_archive_1" from
  // templates/page.kallaren.json's `order` array is what actually stops
  // them from ever running -- see CLAUDE.md's Källare section for why:
  // trivially reversible if Per wants a hybrid later).
  //
  // Built ONCE (osdRendered latch) into window.EvaKallareTV.osdTabs/osdList
  // (DOM refs exposed by the block, not class-name lookups -- this file has
  // no way to know the block's own {{ ai_gen_id }}-suffixed classes) and
  // then just shown/hidden via CSS from then on -- scroll position and the
  // active tab's styling persist for free since the DOM is never rebuilt on
  // open/close, satisfying "returns showing the menu where you left it"
  // with no explicit save/restore logic at all.

  var DECADE_TABS = ['all'].concat(DECADE_ORDER);
  var DECADE_TAB_LABELS = {
    all: 'ALL', '80s': "80'S", '90s': "90'S", '2000s': "00'S", '2010s': "10'S", '2020s': "20'S"
  };

  // Format filter (2026-07-25) -- a SEPARATE dimension from decade, the
  // two combine (both conditions AND together in applyOsdFilter below).
  // kallare-films.json's own `category` field is the existing source of
  // truth here (already used for the row badge/card-style split), not a
  // new schema field: VHS stays VHS, DVD stays DVD, and everything else
  // (currently only ever "Other" in the data -- 41 of 92 films, all
  // youtube/vimeo streams with no physical-media source) maps to DIGITAL.
  // formatOf() is the one place that mapping happens, so badge text and
  // the filter itself can never disagree.
  var FORMAT_TABS = ['all', 'VHS', 'DVD', 'DIGITAL'];
  var FORMAT_TAB_LABELS = { all: 'ALL', VHS: 'VHS', DVD: 'DVD', DIGITAL: 'DIGITAL' };

  function formatOf(film) {
    var c = film.category;
    if (c === 'VHS' || c === 'DVD') { return c; }
    return 'DIGITAL';
  }

  // 79 dots is comfortably wider than the OSD will ever render (a ~340px
  // ceiling at a monospace font) -- .kallare-osd-row-leader's own
  // overflow:hidden clips whatever's left over, so this only needs to be
  // "always long enough," not sized to any particular row width.
  var LEADER_DOTS = new Array(80).join('.');

  var osdRendered = false;
  var osdRows = [];
  var osdVisibleRows = [];
  var osdActiveTab = 'all';
  var osdActiveFormat = 'all';

  // EVA filter (2026-08-24). OFF by default: the OSD is the archive exactly
  // as before and the thirteen section:"EVA" films are hidden everywhere. ON:
  // ONLY those thirteen, in manifest order (osdRows is built straight from
  // the manifest array, so their DOM order already IS the manifest order --
  // nothing needs reordering). The decade/format rows are hidden while ON,
  // and their selections are deliberately NOT reset, so turning the chip off
  // restores whatever the visitor had chosen.
  var osdEvaOnly = false;
  var OSD_ARCHIVE_HEADING = 'SWEDISH SKATEFILM ARCHIVE';
  var OSD_EVA_HEADING = 'EVA FILM ARCHIVE';

  function isEvaFilm(film) { return !!film && film.section === 'EVA'; }
  var osdFocusIndex = -1;
  var osdEmptyEl = null;

  // --- Mobile browse panel (2026-08-09) --------------------------------
  // The mini OSD (see CSS/markup in the block) only fits ~1 row tall on a
  // phone -- this expands the SAME osdEl (header/search/tabs/list/footer,
  // untouched internally) out of the TV screen's tiny clipped rect into a
  // near-full-viewport panel, mobile only (max-width:749px). Reuses the
  // exact FLIP technique popPlayer()/ejectPlayer() already established in
  // the block (measure rect, invert transform, transition to '', cartoon-
  // overshoot cubic-bezier) rather than inventing a new animation
  // mechanism. Deliberately does NOT reparent osdEl to document.body --
  // it stays exactly where it already lives in the DOM (position:fixed
  // still visually escapes the small screen's overflow:hidden clip
  // without moving the node), so a PJAX navigation away destroys it
  // normally with the rest of #MainContent, no manual detach/leak
  // bookkeeping needed (same reasoning this file's own comments give
  // elsewhere for why flatCursorImg -- appended to <body> -- needs
  // special-case cleanup and this doesn't).
  var browseOpen = false;
  var browseSavedScrollY = 0;
  var moreRowEl = null;
  var BROWSE_OPEN_CLASS = 'kallare-osd--browse';
  var BROWSE_BACKDROP_VISIBLE_CLASS = 'kallare-osd-browse-backdrop--visible';

  function isMobileBrowseSupported() {
    try { return window.matchMedia('(max-width: 749px)').matches; } catch (e) { return false; }
  }

  // Standard iOS-safe scroll lock: body becomes position:fixed at its
  // current visual scroll offset (rather than a plain overflow:hidden,
  // which iOS Safari still lets rubber-band/scroll behind on occasion).
  // getBoundingClientRect() reads taken by callers BEFORE this runs stay
  // valid immediately after, since the trick preserves the current visual
  // position by construction -- callers must measure first, THEN lock.
  function lockPageScroll() {
    browseSavedScrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = (-browseSavedScrollY) + 'px';
    document.body.style.left = '0';
    document.body.style.right = '0';
  }

  // Split from unlockPageScroll() below: this half (clearing the lock
  // styles) is also what init()'s unconditional PJAX-navigation teardown
  // calls, since document.body itself persists across a PJAX swap (only
  // #MainContent is replaced) -- a stuck lock would strand the NEXT page
  // scrolled to nowhere. That path deliberately does NOT call
  // window.scrollTo (below) -- forcing the old page's scroll offset onto
  // the incoming page would fight with eva-pjax.js's own scroll handling.
  function unlockPageScrollStyles() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
  }

  function unlockPageScroll() {
    var y = browseSavedScrollY;
    unlockPageScrollStyles();
    window.scrollTo(0, y);
  }

  // Own Audio instance, same reasoning as seatAudio above and
  // insertClunkAudio in the block's own script -- each file owns its own
  // sound instances rather than reaching into another file's closured ones.
  var navTickAudio = null;
  try {
    var uiAssets = window.EvaSoundAssets || {};
    if (uiAssets.hover) {
      navTickAudio = new Audio(uiAssets.hover);
      navTickAudio.preload = 'auto';
      if (window.EvaMute) { window.EvaMute.applyToElement(navTickAudio); }
    }
  } catch (e) {}

  function playNavTick() {
    if (!navTickAudio || isUiSoundMuted()) { return; }
    try {
      navTickAudio.pause();
      navTickAudio.currentTime = 0;
      navTickAudio.volume = 0.12;
      var p = navTickAudio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }

  function updateOsdTabsUI() {
    var tv = window.EvaKallareTV;
    if (!tv || !tv.osdTabs) { return; }
    var buttons = tv.osdTabs.querySelectorAll('.kallare-osd-tab');
    for (var i = 0; i < buttons.length; i++) {
      var isActive = buttons[i].getAttribute('data-tab') === osdActiveTab;
      buttons[i].classList.toggle('kallare-osd-tab--active', isActive);
      buttons[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  }

  function buildOsdTab(tabId) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kallare-osd-tab' + (tabId === osdActiveTab ? ' kallare-osd-tab--active' : '');
    btn.setAttribute('role', 'tab');
    btn.setAttribute('data-tab', tabId);
    btn.setAttribute('aria-selected', tabId === osdActiveTab ? 'true' : 'false');
    btn.textContent = DECADE_TAB_LABELS[tabId] || tabId;
    btn.addEventListener('click', function () {
      if (osdActiveTab === tabId) { return; }
      osdActiveTab = tabId;
      playNavTick();
      updateOsdTabsUI();
      applyOsdFilter();
    });
    return btn;
  }

  function updateOsdFormatTabsUI() {
    var tv = window.EvaKallareTV;
    if (!tv || !tv.osdFormatTabs) { return; }
    var buttons = tv.osdFormatTabs.querySelectorAll('.kallare-osd-tab');
    for (var i = 0; i < buttons.length; i++) {
      var isActive = buttons[i].getAttribute('data-format') === osdActiveFormat;
      buttons[i].classList.toggle('kallare-osd-tab--active', isActive);
      buttons[i].setAttribute('aria-selected', isActive ? 'true' : 'false');
    }
  }

  // Reuses the SAME .kallare-osd-tab class as the decade row (shared CSS,
  // one visual language for both filter rows) -- data-format instead of
  // data-tab is what tells this apart from a decade tab when scoped to
  // tv.osdFormatTabs specifically.
  function buildFormatTab(tabId) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'kallare-osd-tab' + (tabId === osdActiveFormat ? ' kallare-osd-tab--active' : '');
    btn.setAttribute('role', 'tab');
    btn.setAttribute('data-format', tabId);
    btn.setAttribute('aria-selected', tabId === osdActiveFormat ? 'true' : 'false');
    btn.textContent = FORMAT_TAB_LABELS[tabId] || tabId;
    btn.addEventListener('click', function () {
      if (osdActiveFormat === tabId) { return; }
      osdActiveFormat = tabId;
      playNavTick();
      updateOsdFormatTabsUI();
      applyOsdFilter();
    });
    return btn;
  }

  // Sets the search input's width to its own text's REAL rendered pixel
  // width (via the hidden mirror span the block renders alongside it,
  // sharing the same inherited font/letter-spacing) plus a small caret-
  // room buffer -- see this input's own CSS comment in the block for why
  // this replaced a `size`-attribute (character count) approach.
  function updateOsdSearchWidth() {
    var tv = window.EvaKallareTV;
    if (!tv || !tv.osdSearchInput || !tv.osdSearchMeasure) { return; }
    var value = tv.osdSearchInput.value || '';
    // A trailing space measures as 0-width in most browsers -- pad with a
    // hair-space-adjacent placeholder character only when truly empty, so
    // the box never collapses to 0 and the label/cursor gap stays sane.
    tv.osdSearchMeasure.textContent = value.length ? value : ' ';
    var width = tv.osdSearchMeasure.offsetWidth;
    tv.osdSearchInput.style.width = (width + 2) + 'px';
  }

  function updateOsdEmptyState(isEmpty) {
    var tv = window.EvaKallareTV;
    if (!tv || !tv.osdList) { return; }
    if (!osdEmptyEl) {
      osdEmptyEl = document.createElement('div');
      osdEmptyEl.className = 'kallare-osd-empty';
      osdEmptyEl.textContent = 'NO MATCH';
      tv.osdList.appendChild(osdEmptyEl);
    }
    osdEmptyEl.style.display = isEmpty ? '' : 'none';
  }

  function setOsdFocusIndex(index, playSound) {
    if (osdFocusIndex >= 0 && osdVisibleRows[osdFocusIndex]) {
      osdVisibleRows[osdFocusIndex].el.classList.remove('kallare-osd-row--focused');
    }
    osdFocusIndex = index;
    if (osdFocusIndex >= 0 && osdVisibleRows[osdFocusIndex]) {
      osdVisibleRows[osdFocusIndex].el.classList.add('kallare-osd-row--focused');
      osdVisibleRows[osdFocusIndex].el.scrollIntoView({ block: 'nearest' });
      if (playSound) { playNavTick(); }
    }
  }

  // Decade tab, format tab, AND the search query all have to match --
  // "typing filters live" (point 2) applies on top of whichever tabs are
  // active, not instead of them; decade + format themselves also combine
  // (spec: "90'S + VHS shows 90s VHS films"), not override each other.
  // Single place that owns every visible consequence of the EVA chip, so the
  // chip's own state, the heading, the two hidden rows and the list can never
  // drift apart.
  // WHY THE EVA MARK IS SIZED IN CSS, NOT MEASURED (2026-08-24).
  // The spec was "as wide as the ALL chip's box". Measuring the ALL chip at
  // runtime and writing that width onto the mark looks obvious and is a trap:
  // the OSD panel is sized by its content, so a wider EVA row nudges the panel
  // wider, which nudges the decade row and the ALL chip wider too. The
  // feedback is mild (the ALL chip moved 0.4px for a 3.2px change in the mark,
  // ~12%) but a ResizeObserver keeping the two equal still runs away -- it
  // collapsed the mark to 7.5px on desktop and 0.9px at 393px within a few
  // frames. A STATIC width has no such loop, and because the feedback is only
  // ~12% it converged in a single calibration step: the em coefficient in
  // blocks/ai_gen_block_aa57325.liquid now lands the mark within 0.05px of the
  // ALL chip at both desktop and 393px. Re-calibrate that number, do not
  // reintroduce a measure-and-write loop.

  function setOsdEvaOnly(on) {
    var tv = window.EvaKallareTV;
    osdEvaOnly = !!on;
    if (tv && tv.osdEvaChip) {
      tv.osdEvaChip.classList.toggle('kallare-osd-tab--active', osdEvaOnly);
      tv.osdEvaChip.setAttribute('aria-pressed', osdEvaOnly ? 'true' : 'false');
    }
    if (tv && tv.osdHeaderEl) {
      tv.osdHeaderEl.textContent = osdEvaOnly ? OSD_EVA_HEADING : OSD_ARCHIVE_HEADING;
    }
    // Hidden, not emptied -- osdActiveTab/osdActiveFormat keep their values,
    // so the visitor's previous decade/format selection is still applied the
    // moment the chip goes off again.
    if (tv && tv.osdTabs) { tv.osdTabs.style.display = osdEvaOnly ? 'none' : ''; }
    if (tv && tv.osdFormatTabs) { tv.osdFormatTabs.style.display = osdEvaOnly ? 'none' : ''; }
    applyOsdFilter();
  }

  function applyOsdFilter() {
    var tv = window.EvaKallareTV;
    var query = '';
    if (tv && tv.osdSearchInput) { query = (tv.osdSearchInput.value || '').toLowerCase().trim(); }
    var visible = [];
    osdRows.forEach(function (row) {
      var isEva = isEvaFilm(row.film);
      var matchesQuery = !query || row.titleLower.indexOf(query) !== -1;
      var show;
      if (osdEvaOnly) {
        // ONLY the thirteen. Decade/format are ignored entirely (their rows
        // are hidden while ON), search still applies, scoped to these.
        show = isEva && matchesQuery;
      } else {
        // Archive mode: EVA rows are excluded OUTRIGHT, before any other
        // test, so they can never leak in under any decade/format
        // combination -- including the 'all' tab.
        var matchesTab = osdActiveTab === 'all' || row.film.decade === osdActiveTab;
        var matchesFormat = osdActiveFormat === 'all' || formatOf(row.film) === osdActiveFormat;
        show = !isEva && matchesTab && matchesFormat && matchesQuery;
      }
      row.el.style.display = show ? '' : 'none';
      if (show) { visible.push(row); }
    });
    osdVisibleRows = visible;
    updateOsdEmptyState(visible.length === 0);
    setOsdFocusIndex(visible.length ? 0 : -1, false);
    repositionMoreRow();
  }

  // "MORE ▾" row -- mobile-only entry point into the browse panel (see
  // the browse-panel section further below), inserted right after
  // whichever row is CURRENTLY first-visible (re-run on every filter
  // change via applyOsdFilter() above, since "first visible" moves as
  // decade/format/search narrow the list). Deliberately NOT one of
  // osdRows/osdVisibleRows -- it isn't a film, so filtering must never
  // hide or match against it. Click handling for it (and for the rest of
  // the list region) is a single capture listener on tv.osdList, wired
  // once in renderOsd() below -- this row has no listener of its own.
  function buildMoreRow() {
    var el = document.createElement('button');
    el.type = 'button';
    el.className = 'kallare-osd-row kallare-osd-row--more';
    el.setAttribute('aria-hidden', 'true'); // decorative echo of "tap anywhere in this region" -- see renderOsd()'s capture listener
    el.tabIndex = -1;
    el.innerHTML = '<span class="kallare-osd-row-title">MORE ▾</span>';
    return el;
  }

  function repositionMoreRow() {
    var tv = window.EvaKallareTV;
    if (!tv || !tv.osdList) { return; }
    if (!moreRowEl) { moreRowEl = buildMoreRow(); }
    var anchor = osdVisibleRows.length ? osdVisibleRows[0].el.nextSibling : (tv.osdList.firstChild || null);
    if (anchor === moreRowEl) { return; }
    tv.osdList.insertBefore(moreRowEl, anchor);
  }

  // --- Mobile browse panel: open/close (FLIP, mirrors the block's own
  // popPlayer()/ejectPlayer() technique) -----------------------------------
  // openBrowsePanel(): measure the mini OSD's current small rect, add the
  // browse CSS class (jumps it to its big fixed/centered layout), measure
  // THAT as the final rect, then invert -- same measure/invert/transition-
  // to-'' sequence popPlayer() uses, same cartoon-overshoot curve reused
  // rather than a new one ("reuse that timing function, don't invent a
  // new one" per spec).
  function openBrowsePanel() {
    if (browseOpen || !isMobileBrowseSupported()) { return; }
    var tv = window.EvaKallareTV;
    if (!tv || !tv.osdEl || !tv.isPopped || !tv.isPopped() || tv.isAwake()) { return; }
    var osdEl = tv.osdEl;
    var reduceMotion = false;
    try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

    var startRect = osdEl.getBoundingClientRect();
    browseOpen = true;
    lockPageScroll();
    osdEl.classList.add(BROWSE_OPEN_CLASS);
    if (tv.osdBrowseBackdrop) {
      tv.osdBrowseBackdrop.style.display = 'block';
      void tv.osdBrowseBackdrop.offsetWidth; // force reflow so the visible class below actually transitions
      tv.osdBrowseBackdrop.classList.add(BROWSE_BACKDROP_VISIBLE_CLASS);
    }

    var finalRect = osdEl.getBoundingClientRect();
    if (reduceMotion || !startRect.width || !startRect.height || !finalRect.width || !finalRect.height) {
      return;
    }

    var scaleX = startRect.width / finalRect.width;
    var scaleY = startRect.height / finalRect.height;
    var dx = (startRect.left + (startRect.width / 2)) - (finalRect.left + (finalRect.width / 2));
    var dy = (startRect.top + (startRect.height / 2)) - (finalRect.top + (finalRect.height / 2));

    osdEl.style.transition = 'none';
    osdEl.style.transform = 'translateY(-50%) translate(' + dx + 'px, ' + dy + 'px) scale(' + scaleX + ', ' + scaleY + ')';
    void osdEl.offsetWidth; // force reflow so the transition below actually animates
    osdEl.style.transition = 'transform var(--kallare-browse-duration, 0.6s) cubic-bezier(0.68, -0.55, 0.265, 1.55)';
    osdEl.style.transform = '';

    osdEl.addEventListener('transitionend', function onOpenEnd(event) {
      if (event.propertyName !== 'transform') { return; }
      osdEl.removeEventListener('transitionend', onOpenEnd);
      osdEl.style.transition = '';
    });
  }

  // Shared close finisher -- called either after the reverse-FLIP
  // transition (closeBrowsePanel()) or immediately with no animation
  // (closeBrowsePanelInstant(), for eject and PJAX teardown, where a
  // second animation on top of the eject/navigation would just be noise).
  function finishBrowseClose(tv) {
    browseOpen = false;
    if (tv && tv.osdEl) {
      tv.osdEl.classList.remove(BROWSE_OPEN_CLASS);
      tv.osdEl.style.transition = '';
      tv.osdEl.style.transform = '';
    }
    if (tv && tv.osdBrowseBackdrop) {
      tv.osdBrowseBackdrop.classList.remove(BROWSE_BACKDROP_VISIBLE_CLASS);
      tv.osdBrowseBackdrop.style.display = 'none';
    }
  }

  // closeBrowsePanel(): reverse of openBrowsePanel() -- collapses back
  // into the TV's screen rect (tv.bigScreenEl, the SAME box the mini OSD
  // normally fills -- not the small room TV eject targets) and restores
  // the exact scroll position. Used for both real close paths (picking a
  // film, tapping the dimmed backdrop) -- picking a film ALSO lets the
  // click bubble to the row's own listener (playChannel()), unaffected by
  // this running in parallel.
  function closeBrowsePanel() {
    if (!browseOpen) { return; }
    var tv = window.EvaKallareTV;
    if (!tv || !tv.osdEl) { browseOpen = false; unlockPageScroll(); return; }
    var osdEl = tv.osdEl;
    var reduceMotion = false;
    try { reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

    var targetRect = tv.bigScreenEl ? tv.bigScreenEl.getBoundingClientRect() : null;
    var currentRect = osdEl.getBoundingClientRect();

    if (reduceMotion || !targetRect || !targetRect.width || !targetRect.height || !currentRect.width || !currentRect.height) {
      finishBrowseClose(tv);
      unlockPageScroll();
      return;
    }

    var scaleX = targetRect.width / currentRect.width;
    var scaleY = targetRect.height / currentRect.height;
    var dx = (targetRect.left + (targetRect.width / 2)) - (currentRect.left + (currentRect.width / 2));
    var dy = (targetRect.top + (targetRect.height / 2)) - (currentRect.top + (currentRect.height / 2));

    osdEl.style.transition = 'none';
    osdEl.style.transform = 'translateY(-50%)';
    void osdEl.offsetWidth; // force reflow so the transition below actually animates
    osdEl.style.transition = 'transform var(--kallare-browse-duration, 0.6s) cubic-bezier(0.68, -0.55, 0.265, 1.55)';
    osdEl.style.transform = 'translateY(-50%) translate(' + dx + 'px, ' + dy + 'px) scale(' + scaleX + ', ' + scaleY + ')';
    if (tv.osdBrowseBackdrop) { tv.osdBrowseBackdrop.classList.remove(BROWSE_BACKDROP_VISIBLE_CLASS); }

    osdEl.addEventListener('transitionend', function onCloseEnd(event) {
      if (event.propertyName !== 'transform') { return; }
      osdEl.removeEventListener('transitionend', onCloseEnd);
      finishBrowseClose(tv);
      unlockPageScroll();
    });
  }

  // No-animation close -- eject (a second animation racing the reverse-
  // pop would look chaotic) and PJAX navigation teardown (nothing should
  // animate into a page that's being torn down).
  function closeBrowsePanelInstant() {
    if (!browseOpen) { return; }
    var tv = window.EvaKallareTV;
    finishBrowseClose(tv);
    unlockPageScrollStyles();
  }

  function buildOsdRow(film) {
    var el = document.createElement('button');
    el.type = 'button';
    el.className = 'kallare-osd-row';
    el.setAttribute('role', 'option');
    el.setAttribute('data-film-id', film.id);
    el.innerHTML =
      '<span class="kallare-osd-row-title">' + film.title.replace(/</g, '&lt;') + '</span>' +
      '<span class="kallare-osd-row-leader" aria-hidden="true">' + LEADER_DOTS + '</span>' +
      '<span class="kallare-osd-row-badge">' + formatOf(film) + '</span>';
    el.addEventListener('click', function () {
      playChannel(film.id, el);
    });
    return { el: el, film: film, titleLower: film.title.toLowerCase() };
  }

  function updateOsdNowPlaying(filmId) {
    osdRows.forEach(function (row) {
      row.el.classList.toggle('is-playing', !!filmId && row.film.id === filmId);
    });
  }

  function renderOsd(filmList) {
    var tv = window.EvaKallareTV;
    if (!tv || !tv.osdTabs || !tv.osdList) { return; }
    if (osdRendered) { return; } // built once -- see this section's own header comment
    osdRendered = true;

    tv.osdTabs.innerHTML = '';
    DECADE_TABS.forEach(function (tabId) {
      var hasFilms = tabId === 'all' || filmList.some(function (f) { return f.decade === tabId; });
      if (hasFilms) { tv.osdTabs.appendChild(buildOsdTab(tabId)); }
    });

    if (tv.osdFormatTabs) {
      tv.osdFormatTabs.innerHTML = '';
      FORMAT_TABS.forEach(function (tabId) {
        var hasFilms = tabId === 'all' || filmList.some(function (f) { return formatOf(f) === tabId; });
        if (hasFilms) { tv.osdFormatTabs.appendChild(buildFormatTab(tabId)); }
      });
    }

    tv.osdList.innerHTML = '';
    osdRows = filmList.map(buildOsdRow);
    osdRows.forEach(function (row) { tv.osdList.appendChild(row.el); });

    if (tv.osdEvaChip) {
      tv.osdEvaChip.addEventListener('click', function () {
        setOsdEvaOnly(!osdEvaOnly);
      });
      setOsdEvaOnly(false); // establish the OFF presentation up front
    }

    if (tv.osdSearchInput) {
      // Width tracked in real measured px via a hidden mirror span (REBUILT
      // 2026-07-25, third attempt on this bug) -- NOT the `size` attribute
      // (character count), which assumes the input's font resolves to
      // consistent monospace metrics. Root-caused live: a global theme
      // reset (`input{font-size:16px!important}` at mobile widths) was
      // overriding this input's intended font-size, which silently made
      // `size`'s character->px math wrong too. Measuring the ACTUAL
      // rendered width of the current text (in a span sharing this same
      // input's font via CSS inheritance) is correct regardless of which
      // font/size ends up applied -- it can't get out of sync with reality
      // the way a character-count heuristic can.
      updateOsdSearchWidth();
      tv.osdSearchInput.addEventListener('input', function () {
        updateOsdSearchWidth();
        applyOsdFilter();
      });
    }

    // Mobile browse panel entry -- CAPTURE phase (fires while the click is
    // still descending toward its target, before a row's own bubble-phase
    // listener ever runs) on the list container itself covers BOTH
    // "MORE ▾" and "tap anywhere in the film-list region" with one
    // listener: while collapsed, stopPropagation() blocks the click from
    // ever reaching a row's playChannel() listener and opens the panel
    // instead; while already open, a tap on an actual row is left to
    // bubble normally (so playChannel() still fires) but ALSO triggers the
    // panel's own reverse-collapse in parallel -- "collapses back into the
    // TV's screen rectangle... and the film plays." Desktop is untouched:
    // isMobileBrowseSupported() gates both branches to a no-op above 749px,
    // so rows there keep firing playChannel() directly exactly as before.
    if (tv.osdList) {
      tv.osdList.addEventListener('click', function (event) {
        if (!isMobileBrowseSupported()) { return; }
        if (!browseOpen) {
          event.stopPropagation();
          openBrowsePanel();
          return;
        }
        if (event.target.closest && event.target.closest('.kallare-osd-row')) {
          closeBrowsePanel();
        }
      }, true);
    }

    // Tap on the dimmed room outside the panel -- one of the two escape
    // hatches (spec section C), the other being eject (already tappable,
    // see the panel's own z-index reasoning in the block's CSS comment).
    if (tv.osdBrowseBackdrop) {
      tv.osdBrowseBackdrop.addEventListener('click', function () {
        closeBrowsePanel();
      });
    }

    applyOsdFilter();
  }

  // Arrow-key/Enter navigation -- registered ONCE at module scope (this
  // file's IIFE body only runs once per page load, unlike init()), gated on
  // the OSD actually being the active screen content (popped && !awake --
  // isMenuOpen() no longer exists on window.EvaKallareTV since the 2026-07-27
  // rebuild, this is the new state model's equivalent) so it never fires
  // outside the OSD (normal page scrolling/typing elsewhere is untouched).
  // Left/Right and character keys are never preventDefault()'d, so typing in
  // the search input works completely natively -- only Up/Down/Enter are
  // intercepted.
  function onOsdKeydown(event) {
    var tv = window.EvaKallareTV;
    if (!tv || !tv.isPopped || !tv.isPopped() || tv.isAwake()) { return; }
    if (event.key === 'ArrowDown') {
      if (!osdVisibleRows.length) { return; }
      event.preventDefault();
      setOsdFocusIndex(Math.min(osdFocusIndex + 1, osdVisibleRows.length - 1), true);
    } else if (event.key === 'ArrowUp') {
      if (!osdVisibleRows.length) { return; }
      event.preventDefault();
      setOsdFocusIndex(Math.max(osdFocusIndex - 1, 0), true);
    } else if (event.key === 'Enter') {
      if (osdFocusIndex < 0 || !osdVisibleRows[osdFocusIndex]) { return; }
      event.preventDefault();
      var row = osdVisibleRows[osdFocusIndex];
      playChannel(row.film.id, row.el);
    }
  }
  document.addEventListener('keydown', onOsdKeydown);

  // --- Page-wide flat VHS cursor (rest of the Källare page) -------------
  // The room hotspot block (blocks/ai_gen_block_aa57325.liquid) already has
  // its own heavily-tuned proximity-scaled cursor, scoped to its own
  // wrapper (data-kallare-room) -- left completely untouched. This is a
  // simpler, flat (no proximity shrink) follower for everywhere else on the
  // page, using the SAME two images, so cursor coverage reads as continuous
  // down the whole page. Desktop only, same (pointer:fine) gating the room
  // block uses.
  //
  // REPOINTED TWICE now. 2026-07-28: this used to be scoped to the
  // below-the-room archive list (root = [data-kallare-archive-root]),
  // which had carried disabled:true since the 2026-07-26 VCR-OSD rebuild,
  // so it was dead code -- fixed by binding to `document` instead, with
  // the room's own wrapper excluded by closest() lookup. That overshot:
  // Per reported the cursor then followed across the ENTIRE page (header,
  // footer, everywhere), when it should only ever appear inside the room
  // image itself. REPOINTED AGAIN 2026-07-29: bound directly to
  // [data-kallare-room] (the room wrapper) instead of `document` --
  // native mouseenter/mouseleave scoping means the listeners below simply
  // never fire outside that element's own bounds, so outside the room the
  // normal system cursor shows with no JS involved at all. The room's own
  // tuned proximity cursor (blocks/ai_gen_block_aa57325.liquid) still
  // needs its own exclusion WITHIN that bound -- see
  // ROOM_INNER_EXCLUDE_SELECTOR below, now a DIFFERENT, narrower hook
  // than the outer bind target (data-kallare-room-inner, on the room's
  // own artwork container specifically) rather than reusing
  // data-kallare-room for both jobs, which would exclude everywhere it's
  // bound to and never show at all.

  // Module-level singleton, same reasoning as flyingTape above: this file
  // only ever runs once (a plain IIFE, not re-executed per PJAX swap), so
  // the element is created at most once and reused across every visit to
  // this page -- NOT recreated per visit, which would otherwise leak a
  // duplicate <img> (appended to <body>, outside #MainContent, so it'd
  // survive every swap) on every single re-entry.
  var flatCursorImg = null;
  var flatCursorVisible = false;
  var ROOM_SELECTOR = '[data-kallare-room]';
  var ROOM_INNER_EXCLUDE_SELECTOR = '[data-kallare-room-inner]';

  function initPageWideCursor() {
    var isDesktop = false;
    try { isDesktop = window.matchMedia('(pointer: fine)').matches; } catch (e) {}
    if (!isDesktop || reduceMotion) { return; }

    var assets = window.EvaKallareAssets || {};
    if (!assets.cursorNormal) { return; }

    var roomEl = document.querySelector(ROOM_SELECTOR);
    if (!roomEl) { return; }

    if (!flatCursorImg) {
      flatCursorImg = document.createElement('img');
      flatCursorImg.className = 'kallare-flat-cursor';
      flatCursorImg.src = assets.cursorNormal;
      flatCursorImg.alt = '';
      flatCursorImg.setAttribute('aria-hidden', 'true');
      document.body.appendChild(flatCursorImg);
    }

    function show() {
      if (!flatCursorVisible) { flatCursorVisible = true; flatCursorImg.classList.add('kallare-flat-cursor--visible'); }
    }
    function hide() {
      if (flatCursorVisible) { flatCursorVisible = false; flatCursorImg.classList.remove('kallare-flat-cursor--visible'); }
    }

    flatCursorHide = hide;

    // roomEl is fresh every call (recreated by the PJAX swap or the
    // initial page load, since it lives inside #MainContent) -- rebinding
    // here every init() is correct, the OLD element's listeners simply go
    // with it once it's removed from the DOM. Back to the same per-visit
    // rebind pattern the original below-the-room archive root used, now
    // that there's a real, PJAX-swapped element to bind to again.
    roomEl.addEventListener('mouseenter', show);
    roomEl.addEventListener('mouseleave', hide);
    roomEl.addEventListener('mousemove', function (e) {
      // Within the room, the room's OWN tuned proximity cursor (over the
      // TV/hotspot, the big player, and the welcome-text overlay -- see
      // this file's own cursor:auto overrides) already owns the native
      // cursor treatment for that whole inner zone; hide the flat
      // follower there so the two never stack.
      if (e.target.closest && e.target.closest(ROOM_INNER_EXCLUDE_SELECTOR)) { hide(); return; }
      flatCursorImg.style.left = e.clientX + 'px';
      flatCursorImg.style.top = e.clientY + 'px';
      show();
    });
  }

  // Leaving the Källare page entirely (no window.EvaKallareTV on whatever
  // just loaded via PJAX) hides the follower rather than leaving it
  // floating over an unrelated page.
  var flatCursorHide = null;
  document.addEventListener('eva:pjax:loaded', function () {
    if (flatCursorHide && !window.EvaKallareTV) { flatCursorHide(); }
  });

  // --- Init / PJAX ------------------------------------------------------

  function init() {
    // Runs on EVERY PJAX navigation, before the page-detection guard
    // below -- stops anything left running from a PREVIOUS Basement
    // visit (2026-08-02: the EVATV song, film audio, and the play-
    // fallback button were surviving navigation away entirely, since the
    // block's own disconnectedCallback only ever cleaned up its OWN
    // ambience Audio object, never reaching into this file's separate
    // evatvAudio/ytPlayer state). Cheap and safe to run unconditionally,
    // origin/destination page irrelevant: every function called here
    // already no-ops on its own missing state (no evatvAudio, no mount,
    // no ytPlayer), so a navigation that never touched the Basement at
    // all just calls a handful of guarded no-ops. Deliberately does NOT
    // play the eject sound or trigger the block's own reverse-pop
    // animation the way a normal on-page eject (onEject(),
    // 'eva:kallare:eject') does -- the page is about to be replaced
    // either way, so animating anything here is wasted work at best and
    // a visual glitch mid-swap at worst. Radio playback is untouched --
    // none of these reach outside EVATV/film/insert-eject audio.
    stopCurrentPlayback();
    stopEvatvSongFully();
    // Same TV-scoped-mute reset as onEject() -- a fresh page load starts
    // unmuted, same as a fresh re-entry after an on-page eject.
    tvAudioMuted = false;
    updateTvAudioMuteState();
    hidePlayFallback();
    // Mobile browse panel: osdEl itself isn't reparented (see that
    // section's own comment) so it's destroyed normally with the rest of
    // #MainContent -- but document.body persists across a PJAX swap, so
    // its scroll-lock styles (if left applied mid-browse) would strand the
    // NEXT page scrolled to nowhere. No animation, no scrollTo -- see
    // closeBrowsePanelInstant()/unlockPageScrollStyles()'s own comments.
    browseOpen = false;
    unlockPageScrollStyles();

    // Page-detection guard CHANGED 2026-07-26: used to be `if (!root)
    // return` (root = the old below-the-room list section) -- since that
    // section is no longer in templates/page.kallaren.json's `order`
    // array, `root` is now ALWAYS null, which would make this return
    // immediately and never render the OSD at all. window.EvaKallareTV is
    // only ever exposed by blocks/ai_gen_block_aa57325.liquid's
    // connectedCallback(), which only renders on the Källare page --
    // already the exact assumption getMount() elsewhere in this file
    // relies on (that callback runs before this deferred script does).
    if (!window.EvaKallareTV) { return; } // not on the Källare page (or leaving it -- the teardown above already ran either way)

    currentFilmId = null;
    busy = false;
    osdRendered = false;
    osdRows = [];
    osdVisibleRows = [];
    osdActiveTab = 'all';
    osdActiveFormat = 'all';
    osdFocusIndex = -1;
    osdEmptyEl = null;
    moreRowEl = null; // the old DOM node goes with the old page -- see moreRowEl's own comment

    var root = document.querySelector(ROOT_SELECTOR);
    if (root) {
      // Old hybrid-capable list path -- inert in practice (the section
      // isn't in the template's `order` anymore) but left wired in case
      // Per wants a hybrid later, per CLAUDE.md's Källare section.
      fetchFilms(function (filmList) {
        renderArchive(root, filmList);
      });
    }
    // No longer nested inside `if (root)` -- see this function's own
    // comment above it for why that gate was always false in practice.
    initPageWideCursor();

    fetchFilms(function (filmList) {
      renderOsd(filmList);
    });
  }

  ready(init);
  // Each firing hands genuinely fresh DOM (#MainContent's innerHTML was
  // just replaced), so re-running init() unconditionally is correct --
  // no dedup needed, matching this theme's other PJAX-reinit scripts.
  document.addEventListener('eva:pjax:loaded', init);
})();
